import {
  useNavigate,
  useLocation,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useAuth } from "@shared";
import { cn } from "@/lib/utils";
import { AccountSettings } from "./AccountSettings";
import { AuthenticationSettings } from "./AuthenticationSettings";
import { AddressSettings } from "./AddressSettings";
import { EmailPreferencesSettings } from "./EmailPreferencesSettings";
import { ChevronRight, Settings, User, Lock, MapPin, Mail } from "lucide-react";
import { LoadingState, PageHeader, PageContent } from "@printtat/ui";

const SettingsNavItem = ({
  title,
  description,
  isActive,
  onClick,
  icon,
}: {
  title: string;
  description: string;
  isActive: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "w-full text-left px-3 py-2.5 rounded-lg transition-colors",
      isActive
        ? "bg-gray-100 text-gray-900"
        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
    )}
  >
    <span className="flex items-center justify-between">
      <span className="flex items-center gap-2.5">
        <span className="flex-shrink-0">{icon}</span>
        <div>
          <h3 className="text-sm font-medium leading-none">{title}</h3>
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        </div>
      </span>
      {isActive && <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
    </span>
  </button>
);

const SettingsNav = ({ activePath }: { activePath: string }) => {
  const navigate = useNavigate();

  const navItems = [
    {
      title: "Account",
      description: "Manage your profile information",
      path: "/settings/account",
      icon: <User className="h-4 w-4" />,
    },
    {
      title: "Authentication",
      description: "Password and account linking",
      path: "/settings/authentication",
      icon: <Lock className="h-4 w-4" />,
    },
    {
      title: "Addresses",
      description: "Manage shipping addresses",
      path: "/settings/addresses",
      icon: <MapPin className="h-4 w-4" />,
    },
    {
      title: "Email preferences",
      description: "Cart reminders, marketing, and unsubscribes",
      path: "/settings/email",
      icon: <Mail className="h-4 w-4" />,
    },
  ];

  return (
    <div className="space-y-1">
      {navItems.map((item) => (
        <SettingsNavItem
          key={item.path}
          title={item.title}
          description={item.description}
          isActive={activePath === item.path}
          onClick={() => navigate(item.path)}
          icon={item.icon}
        />
      ))}
    </div>
  );
};

export const SettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  // Automatically redirect to sign-in if no user is found
  if (!loading && !user) {
    navigate("/sign-in", { replace: true });
    return null;
  }

  if (loading) {
    return <LoadingState message="Loading settings..." className="py-6 px-8" />;
  }

  if (!user) {
    return null;
  }

  return (
    <PageContent>

      <div className="mt-6 flex gap-8">
        <div className="w-64 flex-shrink-0 lg:sticky lg:top-6 self-start">
                <PageHeader
        icon={Settings}
        title="Settings"
        description="Manage your account preferences"
      />

          <SettingsNav activePath={location.pathname} />
        </div>

        <div className="flex-1 min-w-0">
          <Routes>
            <Route
              path="/"
              element={<Navigate to="/settings/account" replace />}
            />
            <Route path="/account" element={<AccountSettings />} />
            <Route
              path="/authentication"
              element={<AuthenticationSettings />}
            />
            <Route path="/addresses" element={<AddressSettings />} />
            <Route path="/email" element={<EmailPreferencesSettings />} />
          </Routes>
        </div>
      </div>
    </PageContent>
  );
};
